// AI Import dialog — replaces the standalone AgentImportPage 'input' phase.
//
// Two affordances inside the dialog (matches xapi-new MyApisPage.tsx:295–322):
//   1. Paste textarea (raw OpenAPI / Swagger / docs / URL)
//   2. File picker (.json / .yaml / .yml / .txt) — reads as UTF-8 text
//
// Click Analyze → runs agentService.analyzeStream INLINE (modal stays open,
// progress events stream into a log). On success: stash the result in
// sessionStorage and navigate to the existing AgentImportPage which jumps
// straight to its 'review' phase.

import React from 'react';
import { Dialog, DialogHeader, DialogBody } from './dialog.jsx';
import { XApi } from '../services.js';
import { XIcons, XPrim } from '../primitives.jsx';

export const AI_IMPORT_STASH_KEY = 'xapi.aiImport.session';

export const AiImportDialog = ({ open, onClose, onAnalyzed }) => {
  const fileInputRef = React.useRef(null);
  const abortRef = React.useRef(null);
  const { toast } = XPrim.useToast();

  const [rawInput, setRawInput] = React.useState('');
  const [fileLabel, setFileLabel] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [log, setLog] = React.useState([]);
  const [err, setErr] = React.useState(null);

  // Reset on close.
  React.useEffect(() => {
    if (!open) {
      try { abortRef.current && abortRef.current.abort(); } catch {}
      setBusy(false); setProgress(0); setLog([]); setErr(null);
      setFileLabel('');
    }
  }, [open]);

  const onPickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr(null);
    try {
      const text = await file.text();
      setRawInput(text);
      setFileLabel(`${file.name} · ${(text.length / 1024).toFixed(1)} KB`);
    } catch (ex) {
      setErr('Could not read file: ' + (ex.message || ex));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startAnalyze = async () => {
    if (!rawInput.trim()) { setErr('Paste a spec or pick a file first.'); return; }
    setErr(null); setLog([]); setProgress(2); setBusy(true);
    abortRef.current = new AbortController();
    let p = 5;
    try {
      const resp = await XApi.agentService.analyzeStream(rawInput, (progressData) => {
        const msg = (progressData && (progressData.message || progressData.stage)) || 'progress';
        setLog((l) => l.concat([typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 120)]));
        p = Math.min(95, p + 6);
        setProgress(p);
      }, { signal: abortRef.current.signal });
      setProgress(100);

      // Stash for AgentImportPage to pick up; clear after one read.
      try {
        sessionStorage.setItem(AI_IMPORT_STASH_KEY, JSON.stringify({ rawInput, resp, ts: Date.now() }));
      } catch {}

      onAnalyzed && onAnalyzed(resp);
      onClose && onClose();
    } catch (e) {
      if (e && (e.name === 'AbortError' || e.code === 20)) {
        setErr('Canceled');
      } else {
        setErr(e.message || 'Analyze failed');
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const cancelAnalyze = () => {
    try { abortRef.current && abortRef.current.abort(); } catch {}
  };

  return (
    <Dialog open={open} onClose={busy ? null : onClose} maxWidth={640}>
      <DialogHeader title="Import with AI" onClose={busy ? null : onClose}/>
      <DialogBody padding={20}>
        {!busy && (
          <>
            <p style={{fontSize: 12.5, color:'var(--fg-3)', lineHeight: 1.7, margin:'0 0 14px'}}>
              Paste an OpenAPI/Swagger spec, docs dump, or a URL. Or pick a <span className="mono">.json/.yaml/.yml</span> file. The agent extracts endpoints, infers auth and pricing.
            </p>

            <div className="row gap-2" style={{marginBottom: 10}}>
              <input ref={fileInputRef} type="file" accept=".json,.yaml,.yml,.txt,application/json,application/x-yaml" style={{display:'none'}} onChange={onPickFile}/>
              <button className="btn btn-sm" onClick={()=>fileInputRef.current && fileInputRef.current.click()}>
                <XIcons.IconDownload size={12}/> Pick file
              </button>
              {fileLabel && <span className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-3)'}}>{fileLabel}</span>}
              {(rawInput || fileLabel) && (
                <button className="btn btn-sm btn-ghost btn-icon" title="Clear" onClick={()=>{setRawInput(''); setFileLabel('');}}>
                  <XIcons.IconX size={11}/>
                </button>
              )}
            </div>

            <textarea
              className="input mono"
              style={{minHeight: 220, fontSize: 12, fontFamily:'var(--font-mono)'}}
              placeholder={'Paste OpenAPI/Swagger JSON or YAML, a Postman collection, raw docs, or a URL like\nhttps://api.example.com/openapi.json'}
              value={rawInput}
              onChange={e=>{ setRawInput(e.target.value); setErr(null); if (fileLabel) setFileLabel(''); }}
            />

            {err && <div style={{color:'var(--err)', fontSize: 12, marginTop: 10}}>{err}</div>}

            <div className="row between" style={{marginTop: 18}}>
              <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)'}}>
                accepts: openapi 3.x · swagger 2.x · postman v2 · raw text · url
              </div>
              <div className="row gap-2">
                <button className="btn" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={startAnalyze} disabled={!rawInput.trim()}>
                  <XIcons.IconZap size={13}/> Analyze
                </button>
              </div>
            </div>
          </>
        )}

        {busy && (
          <>
            <div className="row between" style={{alignItems:'flex-start', marginBottom: 14}}>
              <div className="row gap-3">
                <div className="spinner" style={{width: 22, height: 22}}/>
                <div>
                  <div style={{fontSize: 14, fontWeight: 500}}>Analyzing with AI agent…</div>
                  <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0}}>{Math.round(progress)}%</div>
                </div>
              </div>
              <button className="btn btn-sm" onClick={cancelAnalyze}>
                <XIcons.IconX size={11}/> Cancel
              </button>
            </div>
            <div className="bar-track"><div className="bar-fill" style={{width: progress + '%', transition:'width 0.3s ease'}}/></div>
            <div className="mono" style={{marginTop: 16, fontSize: 12, color:'var(--fg-3)', lineHeight: 1.8, maxHeight: 280, overflowY:'auto'}}>
              {log.map((l, i) => (
                <div key={i} style={{color: i === log.length - 1 ? 'var(--fg-1)' : 'var(--fg-4)'}}>
                  <span style={{color:'var(--ok)'}}>✓</span> {l}
                </div>
              ))}
              {!log.length && <div style={{color:'var(--fg-4)'}}>Streaming progress events from agent…</div>}
            </div>
          </>
        )}
      </DialogBody>
    </Dialog>
  );
};
